import {
  ApiAttributionModel,
  ApiChartAttribute,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import {
  Box,
  darken,
  FormControlLabel,
  Grid,
  IconButton,
  lighten,
  Stack,
  styled,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import { ColDef } from "ag-grid-community";
import GlassCard from "Components/GlassCard/GlassCard";
import Loading from "Components/Loading/Loading";
import DataTable from "Components/UI/DataTable";
import {
  AttributionMetric,
  formatDimension,
  isTrendDimension,
  metricByAttribution,
  useChartData,
  useFormatMetric,
} from "features/analytics";
import { getCurrencySymbol } from "features/currency";
import { getOrdinalSuffix } from "Helpers/numbers";
import { useSelectedProject } from "Hooks";
import { CallbackOrVal, IChart, TChartData } from "Interfaces";
import { useFromToContext } from "Providers/FromToProvider";
import { useCallback, useMemo, useState } from "react";
import moment from "moment";
import CustomerValueCohortCell from "features/analytics/components/CustomerValueCohortCell";
import { mergeArraysByKey } from "Helpers/arrays";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import CustomerValueConfigDrawer from "features/analytics/components/CustomerValueConfigDrawer";
import useAppendSearchState from "Hooks/useAppendSearchState";
import { Settings, Star } from "@mui/icons-material";
import { StyledFlexBox } from "Components/UI/StylesFlexBox";

const ScaleItem = styled(Box)<{ colour: string }>(({ theme, colour }) => ({
  background: colour,
  flex: 1,
  height: 10,
  borderRight: `1px solid rgba(255,255,255,0.4)`,
}));

interface ICustomerValueCohortProps {
  model: ApiAttributionModel;
}

function CustomerValueCohort({ model }: ICustomerValueCohortProps) {
  const theme = useTheme();
  const { selectedProject } = useSelectedProject();
  const { chartOutput, calculatedFrom } = useFromToContext();
  const formatMetric = useFormatMetric();
  const metricToName = useMetricName();
  const [dimension, setDimension] = useState<ApiDimension>(
    ApiDimension.DIMENSION_FIRST_SALE_MONTH,
  );
  const [showProfitable, setShowProfitable] = useState(false);
  const [showAvgPer, setShowAvgPer] = useState(true);
  const [type, setType] = useState<
    AttributionMetric.CUMULATIVE_LTV | AttributionMetric.CUMULATIVE_LTV_PROFIT
  >(AttributionMetric.CUMULATIVE_LTV);
  const [selectedAttributes, setSelectedAttributes] = useAppendSearchState<
    ApiChartAttribute[]
  >("cat", []);

  const dimension2 = useMemo(() => {
    if (dimension === ApiDimension.DIMENSION_FIRST_SALE_MONTH) {
      return ApiDimension.DIMENSION_SESSION_START_BY_MONTH;
    }
    return dimension;
  }, [dimension]);

  const [open, setOpen] = useState(false);

  const ltvMetric = useMemo(() => {
    return metricByAttribution(
      model || ApiAttributionModel.EBA,
      AttributionMetric.CUMULATIVE_LTV,
    );
  }, [model]);

  const ltvProfitMetric = useMemo(() => {
    return metricByAttribution(
      model || ApiAttributionModel.EBA,
      AttributionMetric.CUMULATIVE_LTV_PROFIT,
    );
  }, [model]);

  const newCustomerMetric = useMemo(() => {
    return metricByAttribution(
      model || ApiAttributionModel.EBA,
      AttributionMetric.NEW_CUSTOMER,
    );
  }, [model]);

  const cacMetric = useMemo(() => {
    return metricByAttribution(
      model || ApiAttributionModel.EBA,
      AttributionMetric.CAC,
    );
  }, [model]);

  const chartLPT: IChart = useMemo(
    () => ({
      dimension: [dimension],
      includePublisherReadyOnly: true,
      yAxisKeys: [
        {
          key: "l",
          fields: [
            ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_FIRST,
            ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LAST,
            ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LAST_NON_DIRECT,
            ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LINEAR,
            ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_POSITION_BASED,
            ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_IMPACTED,
            ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_EBA,
            ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_CUSTOM,
            ApiMetric.METRIC_CUMULATIVE_LTV_FIRST,
            ApiMetric.METRIC_CUMULATIVE_LTV_LAST,
            ApiMetric.METRIC_CUMULATIVE_LTV_LAST_NON_DIRECT,
            ApiMetric.METRIC_CUMULATIVE_LTV_LINEAR,
            ApiMetric.METRIC_CUMULATIVE_LTV_POSITION_BASED,
            ApiMetric.METRIC_CUMULATIVE_LTV_IMPACTED,
            ApiMetric.METRIC_CUMULATIVE_LTV_EBA,
            ApiMetric.METRIC_CUMULATIVE_LTV_CUSTOM,
            ApiMetric.METRIC_CUMULATIVE_LTV_PROFIT_FIRST,
            ApiMetric.METRIC_CUMULATIVE_LTV_PROFIT_LAST,
            ApiMetric.METRIC_CUMULATIVE_LTV_PROFIT_LAST_NON_DIRECT,
            ApiMetric.METRIC_CUMULATIVE_LTV_PROFIT_LINEAR,
            ApiMetric.METRIC_CUMULATIVE_LTV_PROFIT_POSITION_BASED,
            ApiMetric.METRIC_CUMULATIVE_LTV_PROFIT_IMPACTED,
            ApiMetric.METRIC_CUMULATIVE_LTV_PROFIT_EBA,
            ApiMetric.METRIC_CUMULATIVE_LTV_PROFIT_CUSTOM,
          ],
        },
      ],
      attributes: selectedAttributes || [],
      orderBy: `${dimension} ASC`,
      dateDimension: ApiDimension.DIMENSION_FIRST_SALE_DATE,
    }),
    [dimension, selectedAttributes],
  );

  const chartMetrics: IChart = useMemo(
    () => ({
      dimension: [dimension2],
      includePublisherReadyOnly: true,
      yAxisKeys: [
        {
          key: "l",
          fields: [
            ApiMetric.METRIC_SPEND,
            ApiMetric.METRIC_CAC_FIRST,
            ApiMetric.METRIC_CAC_LAST,
            ApiMetric.METRIC_CAC_LAST_NON_DIRECT,
            ApiMetric.METRIC_CAC_LINEAR,
            ApiMetric.METRIC_CAC_POSITION_BASED,
            ApiMetric.METRIC_CAC_IMPACTED,
            ApiMetric.METRIC_CAC_EBA,
            ApiMetric.METRIC_CAC_CUSTOM,
          ],
        },
      ],
      attributes: selectedAttributes || [],
    }),
    [selectedAttributes, dimension2],
  );

  const { chartData: chartData1, loading: loading1 } = useChartData(
    selectedProject,
    chartLPT,
    chartOutput,
    { ignoreModel: true },
  );
  const { chartData: chartData2, loading: loading2 } = useChartData(
    selectedProject,
    chartMetrics,
    chartOutput,
    { ignoreModel: true },
  );

  const chartData = useMemo(() => {
    if (!chartData1?.data || !chartData2?.data) {
      return [];
    }

    const arr = mergeArraysByKey(
      chartData1?.data || [],
      chartData2?.data || [],
      "name",
    );

    return arr;
  }, [chartData1, chartData2]);

  const loading = useMemo(() => loading1 || loading2, [loading1, loading2]);

  const currency = useMemo(
    () =>
      selectedProject?.currency
        ? getCurrencySymbol(selectedProject?.currency)
        : "$",
    [selectedProject],
  );

  const getCohortStartDate = useCallback(
    (d: TChartData) => {
      return isTrendDimension(dimension)
        ? moment(d.name, "MMM YYYY")
        : moment(calculatedFrom, "MMM YYYY");
    },
    [dimension, calculatedFrom],
  );

  const chartOutputTo = chartOutput?.to;
  const toDate = chartOutputTo
    ? moment(chartOutputTo).startOf("month")
    : moment().add(1, "month").startOf("month");
  const maxRange = useMemo(() => {
    if (!chartData || loading || !chartData[0]) {
      return 0;
    }
    const cohortStartDate = getCohortStartDate(chartData[0]);
    return toDate.diff(cohortStartDate, "months");
  }, [chartData, loading, toDate, getCohortStartDate]);

  const rowData = useMemo(() => {
    if (!chartData || loading) {
      return [];
    }

    return chartData.map((d) => {
      const cohortStartDate = getCohortStartDate(d);
      const monthsInRange = toDate.diff(cohortStartDate, "months") + 1;

      let ltvArray = (JSON.parse(`${d[ltvMetric as ApiMetric]}`) || []).sort(
        (a, b) => a[0] - b[0],
      );
      if (!ltvArray.length) {
        ltvArray = [[0, 0]];
      }

      let profitArray = (
        JSON.parse(`${d[ltvProfitMetric as ApiMetric]}`) || []
      ).sort((a, b) => a[0] - b[0]);
      if (!profitArray.length) {
        profitArray = [[0, 0]];
      }

      const output = {};
      let lastLTVValue: number | null = null;
      let lastProfitValue: number | null = null;
      let isProfitableIndex: number = -1;
      const customerCount = Number(d[newCustomerMetric as ApiMetric] || 0);
      const spend = Number(d[ApiMetric.METRIC_SPEND] || 0);
      const cac = Number(d[cacMetric as ApiMetric] || 0);
      for (let i = 0; i < monthsInRange; i++) {
        const foundPairLtv = ltvArray.find(([index]) => index === i);
        if (foundPairLtv) {
          lastLTVValue = foundPairLtv[1];
        }
        const foundPairProfit = profitArray.find(([index]) => index === i);
        if (foundPairProfit) {
          lastProfitValue = foundPairProfit[1];
        }

        const ltvValue = showAvgPer
          ? lastLTVValue && customerCount > 0
            ? (lastLTVValue || 0) / customerCount
            : 0
          : lastLTVValue;
        const profitValue = showAvgPer
          ? lastProfitValue && customerCount > 0
            ? (lastProfitValue || 0) / customerCount
            : 0
          : lastProfitValue;

        if (profitValue && isProfitableIndex === -1) {
          const cost = cac * customerCount;
          const isProfitable = showAvgPer
            ? profitValue > cac
            : profitValue >= cost;
          if (isProfitable) {
            isProfitableIndex = i;
          }
        }

        output[i] =
          type === AttributionMetric.CUMULATIVE_LTV ? ltvValue : profitValue;
      }

      return {
        cohort: d.name,
        [newCustomerMetric as ApiMetric]: customerCount,
        [cacMetric as ApiMetric]: cac,
        [ApiMetric.METRIC_SPEND]: spend,
        profitable_index: isProfitableIndex,
        ...output,
      };
    });
  }, [
    chartData,
    ltvMetric,
    ltvProfitMetric,
    loading,
    showAvgPer,
    getCohortStartDate,
    newCustomerMetric,
    cacMetric,
    type,
    toDate,
  ]);

  const { min, max } = useMemo(() => {
    let values = rowData.flatMap((row) => {
      return Object.entries(row)
        .filter(
          ([key, value]) =>
            !isNaN(Number(key)) &&
            !isNaN(Number(value)) &&
            isFinite(Number(value)) &&
            typeof value === "number",
        )
        .map(([, value]) => Number(value));
    });

    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }, [rowData]);

  const colorScale = useMemo(() => {
    const steps = 12;
    const colorBase = theme.palette.primary.main;
    const midPoint = 6;

    return Array.from({ length: steps }, (_, i) => {
      if (i > midPoint) {
        return darken(colorBase, (i - midPoint) / steps);
      } else if (i < midPoint) {
        return lighten(colorBase, (midPoint - i) / steps);
      } else {
        return colorBase;
      }
    });
  }, [theme]);

  //
  const getCellStyle: (v: any) => any = useCallback(
    (params) => {
      if (params.value === undefined)
        return { backgroundColor: theme.palette.background.light };

      const steps = colorScale.length;
      const normalizedIndex = Math.min(
        steps - 1,
        Math.floor(((params.value - min) / (max - min)) * steps),
      );

      return {
        backgroundColor: colorScale[normalizedIndex],
        color: "#fff",
        borderBottom: "1px solid #fff",
      };
    },
    [colorScale, min, max, theme],
  );

  const cellRenderer = useCallback(
    ({ valueFormatted, colDef, data, value }) => {
      if (!value) {
        return <></>;
      }

      return (
        <CustomerValueCohortCell
          valueFormatted={valueFormatted}
          headerName={colDef.headerName}
          dimensionName={data.cohort}
          firstSale={data[0]}
          value={Number(value)}
          isProfitable={
            showProfitable &&
            Number(colDef.field) === Number(data.profitable_index)
          }
        />
      );
    },
    [showProfitable],
  );

  const cohortWidth = useMemo(() => {
    return dimension === ApiDimension.DIMENSION_FIRST_SALE_MONTH ? 150 : 300;
  }, [dimension]);

  const numCompare = (valueA, valueB) => {
    const valA = isNaN(valueA) ? 0 : valueA;
    const valB = isNaN(valueB) ? 0 : valueB;
    return valA - valB;
  };

  const columns: ColDef[] = useMemo(
    () => [
      {
        headerName: "Cohort",
        field: "cohort",
        pinned: "left",
        width: cohortWidth,
        valueFormatter: ({ value }) => formatDimension(dimension, value),
        comparator: (date1: string, date2: string) =>
          moment(date1).diff(moment(date2)),
      },
      {
        headerName: "New Customers",
        field: newCustomerMetric,
        pinned: "left",
        width: 135,
        comparator: numCompare,
        valueFormatter: ({ value }) =>
          formatMetric(newCustomerMetric as ApiMetric, value),
      },
      {
        headerName: "CAC",
        field: cacMetric,
        pinned: "left",
        width: 120,
        comparator: numCompare,
        valueFormatter: ({ value }) =>
          formatMetric(ApiMetric.METRIC_CAC, value),
      },
      {
        headerName: metricToName(ApiMetric.METRIC_SPEND),
        field: ApiMetric.METRIC_SPEND,
        pinned: "left",
        width: 112,
        comparator: numCompare,
        valueFormatter: ({ value }) =>
          formatMetric(ApiMetric.METRIC_SPEND, value),
      },
      ...Array.from(
        { length: maxRange + 1 },
        (_, i) =>
          ({
            headerName: i === 0 ? "First Sale" : `${getOrdinalSuffix(i)} Month`,
            field: `${i}`,
            minWidth: 120,
            maxWidth: 200,
            cellStyle: getCellStyle,
            valueFormatter: ({ value }) =>
              formatMetric(ApiMetric.METRIC_CUMULATIVE_LTV_LAST, value, {
                isKM: true,
              }),
            cellRenderer: cellRenderer,
          }) as ColDef,
      ),
    ],
    [
      cellRenderer,
      currency,
      dimension,
      formatMetric,
      getCellStyle,
      maxRange,
      metricToName,
      cohortWidth,
      newCustomerMetric,
      cacMetric,
    ],
  );

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);
  if (loading) {
    return <Loading fullHeight />;
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        flexWrap: "nowrap",
        gap: 2,
      }}
    >
      <GlassCard>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={2}>
            <Typography variant="subtitle2">Historical data</Typography>
            <Stack direction={"row"}>
              {colorScale.map((c) => (
                <ScaleItem key={c} colour={c} />
              ))}
            </Stack>
          </Grid>

          <Grid item xs={4.5}>
            <StyledFlexBox width={170} compact>
              <Typography>Profit</Typography>
              <Switch
                disabled={loading}
                checked={type === AttributionMetric.CUMULATIVE_LTV}
                onChange={(e, checked) =>
                  setType(
                    checked
                      ? AttributionMetric.CUMULATIVE_LTV
                      : AttributionMetric.CUMULATIVE_LTV_PROFIT,
                  )
                }
              />
              <Typography>Revenue</Typography>
            </StyledFlexBox>
          </Grid>

          <Grid item xs={5.5}>
            <Stack direction="row" justifyContent={"flex-end"} spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAvgPer}
                    onChange={(e, c) => setShowAvgPer(c)}
                  />
                }
                label="Avg per customer"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showProfitable}
                    onChange={(e, c) => setShowProfitable(c)}
                  />
                }
                label={
                  <>
                    Show When Profitable{"   "}
                    <Star fontSize="extraSmall" />
                  </>
                }
              />
              <IconButton onClick={() => setOpen(true)}>
                <Settings />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      </GlassCard>
      <Box sx={{ flex: 1 }} pb={2}>
        <GlassCard
          boxProps={{
            p: 0,
          }}
          sx={{ height: "100%" }}
        >
          <Stack sx={{ height: "100%" }}>
            <Stack sx={{ height: "100%" }}>
              <DataTable
                rows={rowData}
                colDefs={columns}
                pageSize={0}
                styles={{
                  pinnedBorderColor: "transparent",
                  viewportBorderColor: "white",
                }}
              />
            </Stack>
          </Stack>
        </GlassCard>
      </Box>
      <CustomerValueConfigDrawer
        open={open}
        onClose={onClose}
        dimension={dimension}
        setDimension={setDimension}
        attributes={selectedAttributes as ApiChartAttribute[]}
        setAttributes={
          setSelectedAttributes as (
            v: CallbackOrVal<ApiChartAttribute[]>,
          ) => void
        }
      />
    </Box>
  );
}

export default CustomerValueCohort;
